/**
 * Lotería — PRD §18 Phase 1 game #7. The highest-cultural-resonance game.
 *
 * Mechanic: a 4×3 consumer board shows 12 random cards drawn from a 21-card
 * deck. Every 2.4s a card is drawn from the master deck; if it matches a
 * board card it gets stamped. Win = complete any horizontal row (3 rows ×
 * 4 cards). A `Toma Todo` jackpot card is inserted at a random position in
 * the upper half of the draw deck (positions 8-18) — when it appears every
 * board card is marked and the jackpot fires.
 *
 * Like every other game in `games/`, the winning stockId is decided up-front
 * via the shared `pickPrize` so the rating-weighted probability matches.
 * The lottery visual is theatre — the result is already determined.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n/useT';
import type { GamePlayProps, WheelItem } from './pickPrize';
import { pickPrize } from './pickPrize';
import { GameStage } from './_shared/GameStage';
import { useGameSound } from './_shared/useGameSound';
import { useHaptics } from './_shared/useHaptics';
import { useCelebration } from './_shared/Celebration';
import { withAlpha } from './_shared/colors';

// 21 traditional Lotería cards. SVG-only, scales crisply on any kiosk size.
type CardDef = { id: string; name: string; isJackpot?: boolean };
const DECK: CardDef[] = [
  { id: 'sol', name: 'El Sol' },
  { id: 'luna', name: 'La Luna' },
  { id: 'corazon', name: 'El Corazón' },
  { id: 'arbol', name: 'El Árbol' },
  { id: 'nopal', name: 'El Nopal' },
  { id: 'botella', name: 'La Botella' },
  { id: 'barril', name: 'El Barril' },
  { id: 'rosa', name: 'La Rosa' },
  { id: 'gallo', name: 'El Gallo' },
  { id: 'mano', name: 'La Mano' },
  { id: 'tambor', name: 'El Tambor' },
  { id: 'sirena', name: 'La Sirena' },
  { id: 'escalera', name: 'La Escalera' },
  { id: 'pajaro', name: 'El Pájaro' },
  { id: 'sandia', name: 'La Sandía' },
  { id: 'catrin', name: 'El Catrín' },
  { id: 'muerte', name: 'La Muerte' },
  { id: 'paraguas', name: 'El Paraguas' },
  { id: 'valiente', name: 'El Valiente' },
  { id: 'camaron', name: 'El Camarón' },
  { id: 'melon', name: 'El Melón' },
];
const JACKPOT: CardDef = { id: 'jackpot', name: '¡Toma Todo!', isJackpot: true };

const DRAW_INTERVAL_MS = 2400;
const COUNTDOWN_S = 3;
const ROWS = [
  [0, 1, 2, 3],
  [4, 5, 6, 7],
  [8, 9, 10, 11],
];

function buildBoard(): CardDef[] {
  return [...DECK].sort(() => Math.random() - 0.5).slice(0, 12);
}

function buildDrawDeck(): CardDef[] {
  const shuffled = [...DECK].sort(() => Math.random() - 0.5);
  // PRD §22 fairness — jackpot drops somewhere in positions 8..18 so it's
  // never immediate and never impossible.
  const jackPos = 8 + Math.floor(Math.random() * 11);
  shuffled.splice(jackPos, 0, JACKPOT);
  return shuffled;
}

export function Loteria({ items, primary, accent, onWin }: GamePlayProps) {
  const t = useT();
  const sound = useGameSound();
  const { vibrate } = useHaptics();
  const celebration = useCelebration();
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);

  // Board + draw deck are stable for the duration of a session. New play =
  // remount via key from the wizard.
  const board = useMemo(buildBoard, []);
  const deck = useMemo(buildDrawDeck, []);

  // Winner picked up-front via shared weighted random. Visual layer is just
  // theatre; we always end on this stockId.
  const winnerRef = useRef<WheelItem | null>(null);
  useEffect(() => {
    if (valid.length === 0) return;
    const idx = pickPrize(valid);
    if (idx >= 0) winnerRef.current = valid[idx];
  }, [valid]);

  const [phase, setPhase] = useState<'countdown' | 'playing' | 'won' | 'lost'>(
    'countdown',
  );
  const [countdown, setCountdown] = useState(COUNTDOWN_S);
  const [drawIdx, setDrawIdx] = useState(-1);
  const [marked, setMarked] = useState<number[]>([]);
  const [winRow, setWinRow] = useState<number[] | null>(null);

  // Countdown 3 → 2 → 1 → ¡Lotería! → playing
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 900);
      return () => clearTimeout(t);
    }
    setPhase('playing');
  }, [countdown, phase]);

  // Draw loop — one card every DRAW_INTERVAL_MS
  useEffect(() => {
    if (phase !== 'playing') return;
    const id = setInterval(() => {
      setDrawIdx((d) => {
        const next = d + 1;
        if (next >= deck.length) {
          // Deck exhausted with no win — sanity guard. We force a win here
          // anyway because the wizard expects an `onWin` to finish the round.
          setPhase('lost');
          return d;
        }
        const card = deck[next];
        setMarked((prev) => {
          const newMarks = board.reduce<number[]>((acc, c, i) => {
            if ((c.id === card.id || card.isJackpot === true) && !prev.includes(i)) {
              acc.push(i);
            }
            return acc;
          }, []);
          if (newMarks.length === 0) return prev;
          const allMarked = [...prev, ...newMarks];
          // Check any complete row
          for (const row of ROWS) {
            if (row.every((i) => allMarked.includes(i))) {
              setTimeout(() => setWinRow(row), 200);
              setTimeout(() => setPhase('won'), 900);
              break;
            }
          }
          return allMarked;
        });
        return next;
      });
    }, DRAW_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, deck, board]);

  // Tick each time a new card is called.
  useEffect(() => {
    if (phase === 'playing' && drawIdx >= 0) {
      sound.play('tick');
      vibrate('tick');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawIdx]);

  // Once we hit 'won' (or fallback 'lost') hand the prize back to the wizard
  useEffect(() => {
    if (phase === 'won' || phase === 'lost') {
      if (phase === 'won') {
        celebration.fire();
        sound.play('win');
        vibrate('win');
      }
      const w = winnerRef.current;
      if (w) {
        const id = setTimeout(() => onWin(w), 700);
        return () => clearTimeout(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, onWin]);

  if (valid.length === 0) {
    return (
      <div className="rounded-2xl bg-black/40 p-8 text-center">
        <p className="font-bold">{t('publicSurvey.wheel.emptyTitle')}</p>
        <p className="mt-1 text-sm opacity-70">
          {t('publicSurvey.wheel.emptyBody')}
        </p>
      </div>
    );
  }

  const calledCard = drawIdx >= 0 ? deck[drawIdx] : null;
  const justCalledId = calledCard?.id ?? null;

  return (
    <GameStage
      primary={primary}
      accent={accent}
      sound={sound}
      celebration={celebration}
      maxWidth={440}
      footer={
        <p className="text-center text-[11px] tracking-wide opacity-60">
          {t('publicSurvey.loteria.hint')}
        </p>
      }
    >
      <div className="relative flex w-full flex-col items-center gap-3">
        {phase === 'countdown' && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/80 backdrop-blur-sm">
            <div className="text-center" style={{ animation: 'game-pop-in 300ms both' }}>
              <div className="font-mono text-7xl font-black leading-none" style={{ color: accent }}>
                {countdown || '¡'}
              </div>
              <div className="mt-2 text-sm font-semibold opacity-80">
                {countdown > 0
                  ? t('publicSurvey.loteria.getReady')
                  : t('publicSurvey.loteria.start')}
              </div>
            </div>
          </div>
        )}

        <div className="flex w-full items-center justify-between pr-9 text-[10px] font-bold uppercase tracking-widest opacity-70">
          <span>{t('publicSurvey.loteria.label')}</span>
          <span>
            <span style={{ color: accent }}>{Math.max(0, drawIdx + 1)}</span>
            {' / '}
            {deck.length}
          </span>
        </div>

        {/* Called card display */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest opacity-50">
            {t('publicSurvey.loteria.cardCalled')}
          </span>
          <CalledCardSlot card={calledCard} accent={accent} primary={primary} t={t} />
        </div>

        {/* 4×3 board */}
        <div className="grid w-full grid-cols-4 gap-1.5">
          {board.map((card, i) => (
            <BoardCard
              key={i}
              card={card}
              marked={marked.includes(i)}
              justCalled={card.id === justCalledId}
              winRow={!!winRow?.includes(i)}
              accent={accent}
              primary={primary}
            />
          ))}
        </div>

        {winRow && (
          <div
            className="absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rounded-2xl px-8 py-3 text-center"
            style={{
              backgroundColor: accent,
              color: primary,
              boxShadow: `0 12px 40px -8px ${withAlpha(accent, 0.8)}`,
              animation: 'pop 500ms cubic-bezier(.2,0,0,1) both',
            }}
          >
            <div className="text-2xl font-extrabold tracking-tight">¡LOTERÍA!</div>
          </div>
        )}
      </div>
    </GameStage>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function BoardCard({
  card,
  marked,
  justCalled,
  winRow,
  accent,
  primary,
}: {
  card: CardDef;
  marked: boolean;
  justCalled: boolean;
  winRow: boolean;
  accent: string;
  primary: string;
}) {
  return (
    <div
      className="relative aspect-[3/4] overflow-hidden rounded-md border-2 bg-white"
      style={{
        borderColor: winRow ? accent : marked ? primary : 'rgba(255,255,255,0.18)',
        boxShadow: justCalled ? `0 0 14px ${primary}88` : 'none',
        transition: 'border-color 300ms, box-shadow 300ms',
      }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 p-1">
        <CardIcon id={card.id} color={primary} size={28} />
        <div className="text-center text-[8px] font-bold leading-tight text-[#1B0F2B]">
          {card.name}
        </div>
      </div>
      {marked && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundColor: winRow
              ? `${accent}99`
              : 'rgba(109,40,217,0.62)',
            animation: 'stamp 280ms cubic-bezier(.2,0,0,1)',
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={winRow ? primary : 'rgba(255,255,255,0.95)'}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
      )}
    </div>
  );
}

function CalledCardSlot({
  card,
  accent,
  primary,
  t,
}: {
  card: CardDef | null;
  accent: string;
  primary: string;
  t: (key: string) => string;
}) {
  if (!card) {
    return (
      <div className="grid h-28 w-20 place-items-center rounded-lg border border-dashed border-white/30 text-center">
        <span className="px-1 text-[10px] leading-tight opacity-50">
          {t('publicSurvey.games.loteria.waiting')}
        </span>
      </div>
    );
  }
  const isJackpot = card.id === 'jackpot';
  return (
    <div
      className="flex h-28 w-20 flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border-2 bg-white p-1.5"
      style={{
        borderColor: isJackpot ? accent : primary,
        boxShadow: `0 4px 18px ${isJackpot ? `${accent}55` : `${primary}55`}`,
        animation: 'flipIn 280ms cubic-bezier(.2,0,0,1)',
      }}
    >
      <CardIcon
        id={card.id}
        color={isJackpot ? accent : primary}
        size={40}
      />
      <div
        className="text-center text-[9px] font-bold leading-tight"
        style={{ color: isJackpot ? accent : '#1B0F2B' }}
      >
        {card.name}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Card icons — SVG only so we never ship raster assets. Each takes a
 * `color` prop so the brand kit's accent/primary propagate naturally.
 * ─────────────────────────────────────────────────────────────────── */

function CardIcon({
  id,
  color,
  size = 32,
}: {
  id: string;
  color: string;
  size?: number;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 64 64',
    fill: 'none',
    stroke: color,
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const soft = `${color}33`;
  switch (id) {
    case 'sol':
      return (
        <svg {...common}>
          <circle cx="32" cy="32" r="13" fill={soft} />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const rad = (a * Math.PI) / 180;
            return (
              <line
                key={a}
                x1={32 + 16 * Math.cos(rad)}
                y1={32 + 16 * Math.sin(rad)}
                x2={32 + 22 * Math.cos(rad)}
                y2={32 + 22 * Math.sin(rad)}
                strokeWidth="2.5"
              />
            );
          })}
        </svg>
      );
    case 'luna':
      return (
        <svg {...common}>
          <path
            d="M42 32A18 18 0 0 1 28 14a16 16 0 1 0 0 36 18 18 0 0 1 14-18z"
            fill={soft}
          />
        </svg>
      );
    case 'corazon':
      return (
        <svg {...common}>
          <path
            d="M32 52 Q8 38 8 24a12 12 0 0 1 24-4 12 12 0 0 1 24 4q0 14-24 28z"
            fill={soft}
          />
        </svg>
      );
    case 'arbol':
      return (
        <svg {...common}>
          <path
            d="M32 8 L48 30 H38 L52 48 H12 L26 30 H16 Z"
            fill={soft}
          />
          <line x1="32" y1="48" x2="32" y2="58" />
        </svg>
      );
    case 'nopal':
      return (
        <svg {...common}>
          <ellipse cx="32" cy="38" rx="10" ry="15" fill={soft} />
          <ellipse
            cx="20"
            cy="30"
            rx="8"
            ry="12"
            fill={soft}
            transform="rotate(-30,20,30)"
          />
          <ellipse
            cx="44"
            cy="28"
            rx="8"
            ry="12"
            fill={soft}
            transform="rotate(30,44,28)"
          />
          <line x1="32" y1="53" x2="32" y2="60" />
        </svg>
      );
    case 'botella':
      return (
        <svg {...common}>
          <path
            d="M25 12 L22 22 Q16 26 16 36 L16 52 Q16 56 20 56 L44 56 Q48 56 48 52 L48 36 Q48 26 42 22 L39 12 Z"
            fill={soft}
          />
          <line x1="25" y1="12" x2="39" y2="12" />
        </svg>
      );
    case 'barril':
      return (
        <svg {...common}>
          <ellipse cx="32" cy="18" rx="16" ry="6" fill={soft} />
          <ellipse cx="32" cy="46" rx="16" ry="6" fill={soft} />
          <line x1="16" y1="18" x2="16" y2="46" />
          <line x1="48" y1="18" x2="48" y2="46" />
          {[-6, 0, 6].map((y) => (
            <path key={y} d={`M16 ${32 + y} Q32 ${29 + y} 48 ${32 + y}`} />
          ))}
        </svg>
      );
    case 'rosa':
      return (
        <svg {...common}>
          <circle cx="32" cy="32" r="8" fill={soft} />
          {[0, 72, 144, 216, 288].map((a) => {
            const rad = (a * Math.PI) / 180;
            const cx = 32 + 14 * Math.cos(rad);
            const cy = 32 + 14 * Math.sin(rad);
            return (
              <ellipse
                key={a}
                cx={cx}
                cy={cy}
                rx="8"
                ry="5"
                fill={soft}
                transform={`rotate(${a + 90}, ${cx}, ${cy})`}
              />
            );
          })}
          <line x1="32" y1="40" x2="32" y2="58" />
        </svg>
      );
    case 'gallo':
      return (
        <svg {...common}>
          <ellipse cx="36" cy="36" rx="14" ry="12" fill={soft} />
          <circle cx="48" cy="22" r="8" fill={soft} />
          <path d="M44 18 Q52 12 56 16" />
          <path d="M48 14 Q54 8 58 10" />
          <path d="M42 30 L28 42 L24 56" />
          <path d="M44 48 L52 58" />
        </svg>
      );
    case 'mano':
      return (
        <svg {...common}>
          <path
            d="M20 48 L20 24 Q20 20 24 20 Q28 20 28 24 L28 16 Q28 12 32 12 Q36 12 36 16 L36 18 Q36 14 40 14 Q44 14 44 18 L44 22 Q44 18 48 18 Q52 18 52 22 L52 38 Q52 48 44 52 L28 52 Z"
            fill={soft}
          />
        </svg>
      );
    case 'tambor':
      return (
        <svg {...common}>
          <ellipse cx="32" cy="22" rx="20" ry="8" fill={soft} />
          <ellipse cx="32" cy="42" rx="20" ry="8" fill={soft} />
          <line x1="12" y1="22" x2="12" y2="42" />
          <line x1="52" y1="22" x2="52" y2="42" />
          <line x1="22" y1="14" x2="18" y2="6" />
          <line x1="42" y1="14" x2="46" y2="6" />
        </svg>
      );
    case 'sirena':
      return (
        <svg {...common}>
          <circle cx="32" cy="18" r="10" fill={soft} />
          <path
            d="M22 28 Q32 38 42 28 L44 50 Q38 58 32 56 Q26 58 20 50 Z"
            fill={soft}
          />
          <path d="M20 50 Q16 58 12 56 M44 50 Q48 58 52 56" />
        </svg>
      );
    case 'escalera':
      return (
        <svg {...common}>
          <line x1="16" y1="8" x2="16" y2="56" />
          <line x1="48" y1="8" x2="48" y2="56" />
          {[14, 24, 34, 44].map((y) => (
            <line key={y} x1="16" y1={y} x2="48" y2={y} />
          ))}
        </svg>
      );
    case 'pajaro':
      return (
        <svg {...common}>
          <path d="M8 30 Q18 18 32 24 Q46 18 56 30" />
          <ellipse cx="32" cy="34" rx="12" ry="8" fill={soft} />
          <path d="M32 34 L32 50 M28 50 L32 50 L36 50" />
          <circle cx="40" cy="28" r="3" fill={color} />
        </svg>
      );
    case 'sandia':
      return (
        <svg {...common}>
          <path d="M8 36 A24 24 0 0 1 56 36 Z" fill={soft} />
          <path d="M10 36 Q32 60 54 36" />
          {[
            [22, 30],
            [32, 26],
            [42, 30],
            [27, 38],
            [37, 38],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill={color} />
          ))}
        </svg>
      );
    case 'catrin':
      return (
        <svg {...common}>
          <rect x="20" y="8" width="24" height="18" rx="2" fill={soft} />
          <rect x="16" y="24" width="32" height="4" fill={soft} />
          <path d="M28 28 Q28 40 22 56 M36 28 Q36 40 42 56" />
          <path d="M22 56 L42 56 M24 46 L40 46" />
        </svg>
      );
    case 'muerte':
      return (
        <svg {...common}>
          <circle cx="32" cy="26" r="14" fill={soft} />
          <circle cx="26" cy="22" r="3" />
          <circle cx="38" cy="22" r="3" />
          <path d="M24 32 Q32 38 40 32" />
          <line x1="22" y1="42" x2="24" y2="58" />
          <line x1="42" y1="42" x2="40" y2="58" />
          <line x1="28" y1="42" x2="36" y2="58" />
          <line x1="24" y1="50" x2="40" y2="50" />
        </svg>
      );
    case 'paraguas':
      return (
        <svg {...common}>
          <path d="M8 32 A24 20 0 0 1 56 32" fill={soft} />
          <line x1="32" y1="32" x2="32" y2="56" />
          <path d="M32 56 Q36 56 36 52" />
          {[8, 20, 44, 56].map((x) => (
            <line
              key={x}
              x1={x}
              y1="32"
              x2={32 + (x - 32) * 0.4}
              y2="50"
              strokeDasharray="3 3"
            />
          ))}
        </svg>
      );
    case 'valiente':
      return (
        <svg {...common}>
          <line x1="20" y1="8" x2="44" y2="56" />
          <path d="M16 12 Q20 8 24 12" />
          <rect
            x="14"
            y="30"
            width="20"
            height="4"
            rx="2"
            transform="rotate(55,24,32)"
            fill={soft}
          />
        </svg>
      );
    case 'camaron':
      return (
        <svg {...common}>
          <path
            d="M16 20 Q24 8 40 14 Q54 20 52 36 Q50 50 38 54 Q24 56 18 46 Q10 34 16 20 Z"
            fill={soft}
          />
          <path d="M40 14 Q48 6 50 4 M38 18 Q48 12 52 8 M16 20 Q8 24 8 28" />
        </svg>
      );
    case 'melon':
      return (
        <svg {...common}>
          <ellipse cx="32" cy="36" rx="20" ry="16" fill={soft} />
          <path d="M32 20 L32 12 Q36 10 38 8 M30 13 Q28 8 26 6" />
          {[
            'M16 36 Q32 30 48 36',
            'M14 32 Q32 26 50 32',
            'M16 40 Q32 34 48 40',
          ].map((d, i) => (
            <path key={i} d={d} strokeDasharray="3 4" />
          ))}
        </svg>
      );
    case 'jackpot':
      return (
        <svg {...common}>
          <path
            d="M32 6 L38 22 H56 L42 34 L48 50 L32 40 L16 50 L22 34 L8 22 H26 Z"
            fill={soft}
          />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="32" cy="32" r="18" fill={soft} />
        </svg>
      );
  }
}

